import { useBreakpointValue } from '@genial/utils';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';

import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import { SignedInRootStackParamList } from '../../../routes/types';
import { useTradingActions } from '../../../store/trading/actions';
import {
  tradingCustomerHeroDetailsQuery,
  tradingCustomerPlatformsQuery,
  tradingCustomerTiesQuery,
  tradingListDetailsQuery,
} from '../../../store/trading/selector';

import { CustomerTiesContainer } from './components/CustomerTiesContainer';
import { IntradayContainer } from './components/IntradayContainer';
import { PlatformQueryContainer } from './components/PlatformQueryContainer';
import { TradingDetailsDesktop } from './Desktop';
import { TradingDetailsMobile } from './Mobile';
import { TradingDetailsTablet } from './Tablet';

export namespace TradingDetailsTypes {
  export interface Content {
    selectedTab: string;
    handleTabPress: (tabKey: string) => void;
    tabs: { key: string; label: string; component: JSX.Element }[];
    accountId: number;
    handleRefresh: () => void;
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export const TradingDetails: React.FC<
  NativeStackScreenProps<SignedInRootStackParamList, 'TradingDetails'>
> = ({ route }) => {
  const { accountId, accountPNP, sinacorAccount, accountBP } = {
    accountBP: parseInt(route.params.accountBP, 10),
    accountPNP: parseInt(route.params.accountPNP, 10),
    accountId: parseInt(route.params.accountId, 10),
    sinacorAccount: parseInt(route.params.sinacorAccount, 10),
  };
  const { fetchTradingListItemDetail } = useTradingActions(
    accountId,
    sinacorAccount,
    accountPNP ? accountPNP : accountBP
  );

  const handleTabPress = (tabKey: string) => {
    setSelectedTab(tabKey);
  };

  const tabs = [
    {
      key: 'intraday-position',
      label: 'Posição Intraday',
      component: (
        <IntradayContainer
          accountId={accountId}
          accountPNP={accountPNP}
          sinacor={sinacorAccount}
          accountBP={accountBP}
        />
      ),
    },
    {
      key: 'customer-links',
      label: 'Vínculos do cliente',
      component: <CustomerTiesContainer accountId={accountId} />,
    },
    {
      key: 'platform-query',
      label: 'Consulta de plataformas',
      component: <PlatformQueryContainer accountId={accountId} />,
    },
  ];

  const [selectedTab, setSelectedTab] = useState(tabs[0].key);

  const { refresher: refresherTradingCustomerTies } = useRecoilQuery(
    tradingCustomerTiesQuery({ idConta: accountId })
  );

  const { refresher: refresherTradingCustomerHero } = useRecoilQuery(
    tradingCustomerHeroDetailsQuery({ idConta: accountId })
  );

  const { loading: loadingTradingListDetails, refresher: refresherTradingListDetailsRefresher } =
    useRecoilQuery(
      tradingListDetailsQuery({
        idConta: accountId,
        sinacor: sinacorAccount,
        idContaSuperPNP: accountPNP ? accountPNP : accountBP,
      }),
      {
        cachePolicy: 'no-cache',
      }
    );

  const { refresher: refresherTraddingCustomerPlatforms } = useRecoilQuery(
    tradingCustomerPlatformsQuery({ idConta: accountId })
  );

  const handleRefresh = useCallback(() => {
    if (selectedTab === 'intraday-position') {
      refresherTradingListDetailsRefresher();
    }
    if (selectedTab === 'customer-links') {
      refresherTradingCustomerTies();
    }
    if (selectedTab === 'platform-query') {
      refresherTraddingCustomerPlatforms();
    }
    refresherTradingCustomerHero();
  }, [
    refresherTraddingCustomerPlatforms,
    refresherTradingCustomerHero,
    refresherTradingCustomerTies,
    refresherTradingListDetailsRefresher,
    selectedTab,
  ]);

  const content = useBreakpointValue({
    base: (
      <TradingDetailsMobile
        handleTabPress={handleTabPress}
        selectedTab={selectedTab}
        tabs={tabs}
        accountId={accountId}
        handleRefresh={handleRefresh}
      />
    ),
    sm: (
      <TradingDetailsTablet
        handleTabPress={handleTabPress}
        selectedTab={selectedTab}
        tabs={tabs}
        accountId={accountId}
        handleRefresh={handleRefresh}
      />
    ),
    md: (
      <TradingDetailsDesktop
        handleTabPress={handleTabPress}
        selectedTab={selectedTab}
        tabs={tabs}
        accountId={accountId}
        handleRefresh={handleRefresh}
      />
    ),
  });

  useEffect(() => {
    if (!loadingTradingListDetails) {
      fetchTradingListItemDetail();
    }
  }, [loadingTradingListDetails, fetchTradingListItemDetail]);

  return <ScrollView style={styles.container}>{content}</ScrollView>;
};

TradingDetails.displayName = 'TradingDetails';
